/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Stack,
} from '@mui/material';
import { Lock, Person, Visibility, VisibilityOff, Key } from '@mui/icons-material';

import api from '../../services/api';

export interface ErpAuthModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  title?: string;
  subtitle?: string;
}

export default function ErpAuthModal({
  open,
  onClose,
  onSuccess,
  title = 'Autenticação no ERP Sapiens',
  subtitle = 'Informe suas credenciais do ERP para continuar com esta operação.',
}: ErpAuthModalProps) {
  const [user, setUser] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [saveCredentials, setSaveCredentials] = useState<boolean>(true);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user.trim()) {
      setErrorMessage('Informe o seu usuário do ERP Sapiens.');
      return;
    }

    if (!password.trim()) {
      setErrorMessage('Informe a sua senha do ERP Sapiens.');
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage('');

      await api.post('erp/auth', {
        user: user.trim().toUpperCase(),
        password: password.trim(),
        save: saveCredentials,
      });

      // Sucesso
      setUser('');
      setPassword('');
      setErrorMessage('');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Erro na autenticação ERP:', error);
      const msg =
        error?.response?.data?.message || 'Usuário ou senha incorretos no ERP Sapiens. Verifique suas credenciais.';
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleLoginSubmit}>
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Key color="primary" /> {title}
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {subtitle}
          </Typography>

          {errorMessage && (
            <Alert severity="error" variant="filled" sx={{ mb: 2, fontWeight: 'bold' }}>
              {errorMessage}
            </Alert>
          )}

          <Stack spacing={2} pt={0.5}>
            <TextField
              fullWidth
              size="small"
              label="Usuário ERP (Sapiens) *"
              value={user}
              onChange={e => setUser(e.target.value.toLowerCase())}
              disabled={isLoading}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              size="small"
              label="Senha ERP *"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(prev => !prev)} edge="end">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={saveCredentials}
                  onChange={e => setSaveCredentials(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Lembrar minhas credenciais no sistema
                </Typography>
              }
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <Lock />}
            sx={{ fontWeight: 'bold', px: 3 }}
          >
            Autenticar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
